import { ScheduleFrequencies, Weekdays, DaysOfWeek } from "constants/reportSubscriptions";

const dayShort = (day) => DaysOfWeek.find((d) => d.value === day)?.short || day;

// Pass `t` to localize; omit it (auto-name path) to keep the English strings.
const formatDays = (frequency, days = [], t) => {
	if (frequency === ScheduleFrequencies.Daily) {
		const isWeekdays =
			days.length === Weekdays.length &&
			Weekdays.every((d) => days.includes(d));
		if (isWeekdays) return t ? t("label.everyWeekday") : "every weekday";
		if (days.length === 7) return t ? t("label.everyDay") : "every day";
	}
	if (!days.length) return t ? t("label.noDaysSelected") : "no days selected";
	const list = days.map(dayShort).join(" & ");
	return t ? t("label.everyDays", { days: list }) : `every ${list}`;
};

// Convert an "HH:mm" (24-hour) string into a friendly "h:mm AM/PM" label.
// Falls back to the raw input if the value isn't in the expected shape.
const formatTime = (timeValue, t) => {
	if (!timeValue) return "";
	const match = /^(\d{1,2}):(\d{2})/.exec(timeValue);
	if (!match) return timeValue;
	const hours24 = parseInt(match[1], 10);
	const minutes = match[2];
	const isAM = hours24 < 12;
	const period = t ? t(isAM ? "label.am" : "label.pm") : isAM ? "AM" : "PM";
	const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
	return `${hours12}:${minutes} ${period}`;
};

// Strips the IANA region prefix to produce a short label, e.g. "Australia/Sydney" -> "Sydney"
const shortTimezone = (timezone) => {
	if (!timezone) return "";
	const parts = timezone.split("/");
	return parts[parts.length - 1].replace(/_/g, " ");
};

export const formatScheduleSummary = ({
	name,
	reportName,
	format,
	frequency,
	days,
	time,
	timezone,
	email,
}) => {
	const reportLabel = reportName && format ? `${reportName} (${format})` : reportName || "Report";
	const heading = name ? `${name} — ${reportLabel}` : `${reportLabel} with current filters`;
	const cadence = `Delivered ${formatDays(frequency, days)} at ${formatTime(time)} ${shortTimezone(timezone)}`.trim();
	const recipient = email ? `to ${email}` : "";
	return [heading, cadence, recipient].filter(Boolean).join("\n");
};

export const formatScheduleLine = ({ frequency, days, time, timezone }, t) => {
	const daysStr = formatDays(frequency, days, t);
	const timeStr = formatTime(time, t);
	const tz = shortTimezone(timezone);
	return t
		? t("label.scheduleLine", { days: daysStr, time: timeStr, timezone: tz }).trim()
		: `${daysStr} at ${timeStr} ${tz}`.trim();
};
